import { Tab } from "@/components/atoms/Tab/Tab";
import { ToggleSwitch } from "@/components/atoms/ToggleSwitch/ToggleSwitch";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { MyEvolutionLandingPage } from "@/components/features/MyEvolutions/MyEvolutionLandingPage";
import { ChainedOrStandAlone } from "@/components/molecules/ChainedOrStandAlone/ChainedOrStandAlone";
import { PageHeader } from "@/components/molecules/PageHeader/PageHeader";
import { OnHandleCompleteProps } from "@/components/organisms/Evolutions/EvolutionsCard/EvolutionsCard";
import { MyEvolvedPlayers } from "@/components/organisms/Evolutions/MyEvolvedPlayers/MyEvolvedPlayers";
import LoadingPlayersList from "@/components/organisms/PlayersList/LoadingPlayersList";
import { PlayerList } from "@/components/organisms/PlayersList/PlayersList";
import {
  PlayersSortByType,
  PlayersToolbar,
} from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { InfoIcon } from "@/designSystemIcons";
import { fireGTMEvent } from "@/functions/fireGTMEvent";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { evoMenu } from "@/icons";
import {
  evolutionsSelector,
  fetchEvolutions,
  markEvoCompleteThunk,
  markEvoUncompleteThunk,
} from "@/redux/evolutions/evolutionsSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import {
  MetaRatingsPlayer,
  PlayerRole,
  clearAllFilters,
  clearFilter,
  fetchPlayers,
  getMyEvolutionPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import {
  BREAKPOINTS,
  EXCLUDE_COMPLETED_EVOLUTIONS,
  EXCLUDE_PAID_EVOLUTIONS,
} from "@/settings";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import Pagination from "../../components/molecules/Pagination/Pagination";
import useDebounce from "../../hooks/useDebounce";
import EvolutionsSection from "../Players/EvolutionsSection/EvolutionsSection";
import FiltersSidebar from "../Players/FiltersSidebar/FiltersSidebar";
import { SelectPlayerRole } from "../Players/SelectPlayerRole/SelectPlayerRole";

type DropdownType = "playerRole" | "filters" | "none";

export const EvolutionsPage = () => {
  const maxPageWidth = "max-w-[1672px]";
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  const location = useLocation();
  const [evoCombinationType, setEvoCombinationType] = useState<
    "only-selected" | "chained"
  >("chained");
  const [excludePaidEvo, setExcludePaidEvo] = useState(
    localStorage.getItem(EXCLUDE_PAID_EVOLUTIONS) === "true",
  );
  const [excludeCompletedEvolutions, setExcludeCompletedEvolutions] = useState(
    localStorage.getItem(EXCLUDE_COMPLETED_EVOLUTIONS) === "true",
  );
  const [showExpandedEvolutions, setShowExpandedEvolutions] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  let {
    players: currentPlayers,
    totalPages,
    playersStatus,
    myEvolvedPlayersStatus,
    playerFilters,
  } = useSelector(playersSelector);
  const [page, setPage] = useState(1);
  const [activeEvolutions, setActiveEvolutions] =
    useState<URLSearchParams>(searchParams);
  const { evolutions } = useSelector(evolutionsSelector);
  const [dropdown, setDropdown] = useState<DropdownType>("none");
  const { playerRoles } = useSelector(playersSelector);
  const [playerRole, setPlayerRole] = useState<PlayerRole>();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const queryParams = useSelector(playerFiltersAsQueryParamsSelector);

  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const stableQueryParams = useMemo(
    () => queryParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queryParams)],
  );
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sortBy, setSortBy] = useState<PlayersSortByType>("meta-rating");
  const [player, setPlayer] = useState<MetaRatingsPlayer>();
  const [players, setPlayers] = useState<MetaRatingsPlayer[]>(currentPlayers);

  const refPlayerRole = useOutsideClick(() => {
    if (dropdown === "playerRole") setDropdown("none");
  });

  const refFilters = useOutsideClick(() => {
    if (dropdown === "filters") {
      setDropdown("none");
    }
  });

  useEffect(() => {
    dispatch(fetchEvolutions());
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearAllFilters());
    };
  }, [dispatch]);

  const handleActiveEvolutions = (evolutions: URLSearchParams) => {
    const isEvoSelected = Array.from(evolutions.entries()).length > 0;
    if (isEvoSelected && evoCombinationType === undefined) {
      setEvoCombinationType("only-selected");
    }
    setPage(1);
    setActiveEvolutions(evolutions);
  };

  const handleDropdown = (inputDropdown: DropdownType) => {
    setDropdown(dropdown === inputDropdown ? "none" : inputDropdown);
  };

  // Use the URL filters hook
  const { hasInitializedFromUrl, playerRolesReady, updateUrlFromFilters } =
    useUrlFilters({
      playerRoles,
      playerFilters,
      onPlayerRoleChange: setPlayerRole,
    });

  // Update URL when filters change (only after initialization and playerRoles are ready)
  useEffect(() => {
    if (hasInitializedFromUrl && playerRolesReady && !resourceId) {
      updateUrlFromFilters(
        playerRole ?? "any",
        ownedOrEvolvedFilters,
        stableQueryParams,
      );
    }
  }, [
    playerRole,
    ownedOrEvolvedFilters,
    stableQueryParams,
    updateUrlFromFilters,
    hasInitializedFromUrl,
    playerRolesReady,
    resourceId,
  ]);

  useEffect(() => {
    setPlayers(currentPlayers);
  }, [currentPlayers]);

  useEffect(() => {
    setPlayer(
      players.find(
        (p) =>
          p.eaPlayerId === +resourceId ||
          (p.resourceId === +resourceId && !p.eaPlayerId),
      ),
    );
  }, [players, resourceId]);

  useEffect(() => {
    setPage(1);
  }, [stableQueryParams]);

  useEffect(() => {
    if (canUseClubImport) {
      dispatch(
        getMyEvolutionPlayers({ sort: sortBy, playerRoleId: playerRole?.id }),
      );
    }
  }, [dispatch, sortBy, playerRole, canUseClubImport]);

  const handleFetchPlayers = useCallback(() => {
    dispatch(
      fetchPlayers({
        page,
        sort: sortBy,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        chainedEvo: evoCombinationType === "chained",
        excludePaidEvo: excludePaidEvo,
        excludeCompletedEvolutions: excludeCompletedEvolutions,
        playerRoleId: playerRole?.id,
        queryParams: stableQueryParams,
        evolutions: true,
        evoIds: activeEvolutions
          ?.get("active")
          ?.split(",")
          ?.flatMap((evolutionName) =>
            evolutions
              .filter(
                (e) =>
                  e.name
                    .toLowerCase()
                    .replaceAll(" ", "-")
                    .replaceAll(",", "") === evolutionName,
              )
              .map((e) => e.id),
          ),
        searchText: debouncedSearchTerm,
      }),
    );
  }, [
    dispatch,
    page,
    sortBy,
    playerRole,
    stableQueryParams,
    activeEvolutions,
    excludePaidEvo,
    excludeCompletedEvolutions,
    evoCombinationType,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    handleFetchPlayers();
  }, [handleFetchPlayers]);

  const handleExcludeCompletedEvolutions = () => {
    if (!excludeCompletedEvolutions && !evolutions.some((e) => e.completed)) {
      dispatch(
        addAlert({
          description:
            "You have no completed evolutions. You can manually mark your completed evolutions.",
          type: "info",
        }),
      );
    }
    setExcludeCompletedEvolutions((prev) => {
      localStorage.setItem(EXCLUDE_COMPLETED_EVOLUTIONS, (!prev).toString());
      return !prev;
    });
  };

  const handleExcludePaidEvolutions = () => {
    setExcludePaidEvo((prev) => {
      localStorage.setItem(EXCLUDE_PAID_EVOLUTIONS, (!prev).toString());
      return !prev;
    });
  };

  const handleSelectPlayerRole = (pr: PlayerRole) => {
    fireGTMEvent("added_player_role");
    setPage(1);
    setPlayerRole(pr);
  };

  const onHandleComplete: OnHandleCompleteProps = async ({
    id,
    categoryId,
    completed,
  }) => {
    await dispatch(
      completed
        ? markEvoCompleteThunk({ id, categoryId })
        : markEvoUncompleteThunk({ id, categoryId }),
    );
    if (excludeCompletedEvolutions) {
      handleFetchPlayers();
    }
  };

  return resourceId ? (
    <Outlet context={player} />
  ) : (
    <main className="min-h-screen mx-auto bg-page-gradient">
      <div ref={refTopBar} />
      <>
        <PageHeader
          title={
            location.pathname.includes("my-evolutions")
              ? "EA FC 25 Evolutions and My Evolutions"
              : "EA FC 25 Evolutions and Evolution Players"
          }
          isMobile={isMobile}
          maxWidth={maxPageWidth}
          color="bg-evolution-primary"
          helmetTitle="EA FC 25 Evolutions - EasySBC"
          helmetContent="Explore all EA FC 25 Evolutions. See the best choices for Evolutions and the optimal evolution path for any player instantly with our exclusive Meta Ratings."
          helmetLink="https://www.easysbc.io/evolutions"
        >
          <div className="flex flex-row h-full w-full mobile:w-auto">
            <Tab
              isActive={!location.pathname.includes("my-evolutions")}
              tabColorActive={"bg-evolution-hover"}
              title={"Evolution Players"}
              onClick={() => {
                navigate("/evolutions");
                setSearchTerm("");
                setSortBy("meta-rating");
                setPlayerRole(undefined);
              }}
            />
            <Tab
              isActive={location.pathname.includes("my-evolutions")}
              title={"My Evolutions"}
              tabColorActive={"bg-evolution-hover"}
              onClick={() => {
                dispatch(clearAllFilters());
                setSearchTerm("");
                setSortBy("meta-rating");
                setPlayerRole(undefined);
                navigate("/evolutions/my-evolutions");
              }}
            />
          </div>
        </PageHeader>
        {!location.pathname.includes("my-evolutions") ? (
          <div
            className={clsx(
              "flex flex-col max-mobile:gap-y-2 mx-auto items-center desktop:px-8 desktop:mt-4",
              maxPageWidth,
            )}
          >
            <section className="relative z-10 flex flex-row justify-end w-full m-auto mobile: px-2">
              {isMobile ? (
                <div className="flex flex-col w-full gap-y-2">
                  <div className="flex flex-wrap justify-between mt-2">
                    <Tooltip
                      content={
                        <div className="w-64 py-2">
                          If you have Club Import and{" "}
                          <span className="italic">
                            allow automatic updates
                          </span>{" "}
                          turned on (under the Profile page), we track your
                          completed evolutions based on the evolutions you have
                          available in the EA Web App.
                        </div>
                      }
                      side="bottom"
                      delayDuration={200}
                    >
                      <div className="flex items-center small-mobile:gap-1 cursor-default">
                        <span className="text-sm whitespace-nowrap">
                          Exclude Completed Evos
                        </span>
                        <ToggleSwitch
                          disabled={!isLoggedIn}
                          onChange={() => {
                            if (
                              !excludeCompletedEvolutions &&
                              !evolutions.some((e) => e.completed)
                            ) {
                              dispatch(
                                addAlert({
                                  description:
                                    "You have no completed evolutions. You can manually mark your completed evolutions.",
                                  type: "info",
                                }),
                              );
                            }
                            handleExcludeCompletedEvolutions();
                          }}
                          onClick={() => {
                            if (!isLoggedIn) {
                              dispatch(
                                showModal({
                                  modalType: "login-required",
                                  freeFeatureLoggedInRequiredHook:
                                    "complete-evo",
                                }),
                              );
                            }
                          }}
                          isActive={excludeCompletedEvolutions}
                          isSmall={true}
                          activeColor="bg-primary-green"
                        />
                      </div>
                    </Tooltip>
                    <div className="flex items-center small-mobile:gap-1">
                      <span className="text-sm whitespace-nowrap">
                        Exclude Paid Evos
                      </span>
                      <ToggleSwitch
                        onChange={handleExcludePaidEvolutions}
                        isActive={excludePaidEvo}
                        isSmall={true}
                        activeColor="bg-primary-green"
                      />
                    </div>
                  </div>
                  <div className="flex flex-row justify-between">
                    <button
                      className={clsx(
                        "h-9 w-26 px-2 border-[1.5px] rounded-lg flex flex-row items-center justify-center",
                        " gap-x-1 touch-device:gap-x-2 bg-evolution-primary active:border-evolution-stroke whitespace-nowrap",
                        showExpandedEvolutions
                          ? "border-evolution-stroke"
                          : "border-evolution-primary",
                      )}
                      onClick={() => setShowExpandedEvolutions((prev) => !prev)}
                    >
                      <span
                        className={clsx("text-sm text-evolution-secondary")}
                      >
                        Detailed View
                      </span>
                    </button>
                    {activeEvolutions.size > 0 && (
                      <ChainedOrStandAlone
                        evoCombinationType={evoCombinationType}
                        setEvoCombinationType={(v) => {
                          setEvoCombinationType(v);
                          fireGTMEvent("clicked_chained_or_only_selected");
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-row items-center gap-x-8 mb-4">
                  {activeEvolutions.size > 0 && (
                    <ChainedOrStandAlone
                      evoCombinationType={evoCombinationType}
                      setEvoCombinationType={(v) => {
                        setEvoCombinationType(v);
                        fireGTMEvent("clicked_chained_or_only_selected");
                      }}
                    />
                  )}
                  <div className="flex items-center gap-x-2">
                    <Tooltip
                      content={
                        <div className="w-64 py-2">
                          If you have Club Import and{" "}
                          <span className="italic">
                            allow automatic updates
                          </span>{" "}
                          turned on (under the Profile page), we track your
                          completed evolutions based on the evolutions you have
                          available in the EA Web App.
                        </div>
                      }
                      side="bottom"
                      delayDuration={200}
                    >
                      <div className="cursor-default">
                        <InfoIcon
                          width="12"
                          height="12"
                          outerFill="var(--color-dark-secondary)"
                        />
                      </div>
                    </Tooltip>
                    <span className="text-sm">Exclude Completed Evos</span>
                    <ToggleSwitch
                      onChange={handleExcludeCompletedEvolutions}
                      onClick={() => {
                        if (!isLoggedIn) {
                          dispatch(
                            showModal({
                              modalType: "login-required",
                              freeFeatureLoggedInRequiredHook: "complete-evo",
                            }),
                          );
                        }
                      }}
                      disabled={!isLoggedIn}
                      isActive={excludeCompletedEvolutions}
                      isSmall={true}
                      activeColor="bg-primary-green"
                    />
                  </div>
                  <div className={"flex items-center gap-x-2"}>
                    <span className="text-sm">Exclude Paid Evos</span>
                    <ToggleSwitch
                      onChange={handleExcludePaidEvolutions}
                      isActive={excludePaidEvo}
                      isSmall={true}
                      activeColor="bg-primary-green"
                    />
                  </div>
                  <div>
                    <button
                      onClick={() => setShowExpandedEvolutions((prev) => !prev)}
                      className={clsx(
                        "h-7 px-2 border-[1.5px] rounded-lg flex flex-row items-center justify-center gap-x-1 touch-device:gap-x-2 bg-evolution-primary active:border-evolution-stroke shadow-default-drop",
                        showExpandedEvolutions
                          ? "border-evolution-stroke"
                          : "border-evolution-primary",
                      )}
                    >
                      <div className="text-sm text-evolution-secondary text-nowrap ">
                        Detailed View
                      </div>
                      <div>
                        <span className={"text-evolution-secondary"}>
                          {evoMenu}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </section>
            <div
              className={clsx("relative w-full pl-2 pb-2", isMobile && "pb-0")}
            >
              <p className="absolute hidden text-xs -top-[16.5px] ml-1 text-evolution-secondary touch-device:flex">
                Active Evolutions
              </p>
              <EvolutionsSection
                activeEvolutions={activeEvolutions}
                setActiveEvolutions={handleActiveEvolutions}
                showExpandedEvolutions={showExpandedEvolutions}
                setShowExpandedEvolutions={setShowExpandedEvolutions}
                excludePaid={excludePaidEvo}
                isMobile={isMobile}
                excludeCompletedEvolutions={excludeCompletedEvolutions}
                onHandleComplete={onHandleComplete}
              />
            </div>
            <PlayersToolbar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              dropdown={dropdown}
              handleDropdown={handleDropdown}
              status={playersStatus}
              isTablet={isTouchDevice}
              isMobile={isMobile}
              hasFilters={hasFilters}
              sortBy={sortBy}
              setSortBy={(v) => {
                setSortBy(v);
                fireGTMEvent("sorted_players");
              }}
              playerFilters={playerFilters}
              playerRole={playerRole}
              onUpdateUsingOwnedPlayers={(f) => {
                if (canUseClubImport) {
                  dispatch(updateFilter(f));
                } else {
                  dispatch(
                    showModal({
                      modalType: "premium-feature",
                      premiumFeatureHook: "see-owned-evo-players",
                    }),
                  );
                }
              }}
            />

            <section
              className={clsx(
                "w-full mx-auto text-white p-3 mobile:px-3 px-2 rounded-b-xl flex flex-col gap-y-1 bg-primary-dark",
              )}
            >
              {(playersStatus === "pending" || playersStatus === "idle") &&
              (players.length === 0 || isTouchDevice) ? (
                <LoadingPlayersList />
              ) : players.length === 0 ? (
                <div className="text-center text-white text-sm">
                  No players found
                </div>
              ) : (
                <PlayerList
                  players={players}
                  show3Chem={sortBy === "meta-rating-3-chem"}
                />
              )}
            </section>

            {players.length > 0 && (
              <div className="flex justify-end w-full mb-8 md-lg:pr-8">
                <Pagination
                  currentPage={page}
                  totalAmountOfPages={totalPages}
                  changePage={(p) => {
                    window.scrollTo(0, 0);
                    setPage(p);
                  }}
                />
              </div>
            )}
          </div>
        ) : canUseClubImport ? (
          <MyEvolvedPlayers
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            dropdown={dropdown}
            handleDropdown={handleDropdown}
            status={myEvolvedPlayersStatus}
            isTablet={isTouchDevice}
            isMobile={isMobile}
            hasFilters={hasFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
            playerRole={playerRole}
          />
        ) : localStorage.getItem("canUseClubImport") !== "true" ? (
          <MyEvolutionLandingPage
            maxPageWidth={maxPageWidth}
            onClickCTA={() => navigate("/subscription")}
            ctaText="Go to Subscriptions"
          />
        ) : (
          <></>
        )}
        {dropdown === "filters" ? (
          <div ref={refFilters}>
            <FiltersSidebar
              isTablet={isTouchDevice}
              handleClose={() => handleDropdown("none")}
              playerFilters={playerFilters}
              disabledFilters={["rarity", "price"]}
              onClearFilter={(key) => dispatch(clearFilter(key))}
              onUpdateFilter={(f) => {
                fireGTMEvent("added_filter");
                dispatch(updateFilter(f));
              }}
              hasActiveFilters={hasFilters}
              onClearFilters={() => dispatch(clearAllFilters())}
              parentRoute="evolutions"
            />
          </div>
        ) : dropdown === "playerRole" ? (
          <div ref={refPlayerRole}>
            <SelectPlayerRole
              playerRoles={playerRoles}
              selectedPlayerRole={playerRole}
              handleSelectPlayerRole={(role) => {
                if (role && sortBy === "rating") {
                  setSortBy("meta-rating");
                }
                handleSelectPlayerRole(role);
              }}
              handleOnClose={() =>
                dropdown === "playerRole" ? setDropdown("none") : null
              }
              shouldStickToTop={scrollPastTopBar}
            />
          </div>
        ) : (
          <></>
        )}
      </>
    </main>
  );
};

export default EvolutionsPage;
